// ============================================
// COMPONENT - Desglose de ventas por medio de pago + composición del efectivo esperado
// ============================================
// Mismo desglose que mostraba el módulo viejo (ventasEfectivo, ventasDebito, ...)
// pero como lista dinámica: no depende de una lista fija de medios de pago.

import { useState } from 'react';
import { Card, CardContent } from '@almacenes/ui';
import { MdPayments, MdExpandMore, MdExpandLess } from 'react-icons/md';
import type { TurnoCaja } from '@almacenes/core';
import { formatCurrency, formatMedioPago } from '../utils/formato';

interface DesglosePorMedioPagoProps {
  turno: TurnoCaja;
  titulo?: string;
}

export const DesglosePorMedioPago = ({ turno, titulo = 'Ventas por medio de pago' }: DesglosePorMedioPagoProps) => {
  const [mostrarComposicion, setMostrarComposicion] = useState(false);
  const entradas = Object.entries(turno.ventasPorMedioPago).filter(([, monto]) => monto > 0);
  const efectivo = entradas.filter(([medioPago]) => medioPago === 'EFECTIVO');
  const otros = entradas.filter(([medioPago]) => medioPago !== 'EFECTIVO');

  const montoInicial = turno.montoInicial;
  const efectivoVentas = efectivo[0]?.[1] ?? 0;
  const ingresosAprobados = turno.totalIngresos ?? 0;
  // Solo los ingresos pagados en efectivo suman al cajón (un abono con débito o transferencia no).
  const ingresosEfectivo = turno.totalIngresosEfectivo ?? 0;
  const egresosAprobados = turno.totalEgresos ?? 0;
  const efectivoEsperado = turno.efectivoEsperado ?? montoInicial + efectivoVentas + ingresosEfectivo - egresosAprobados;

  return (
    <Card>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-2">{titulo}</p>
          {entradas.length === 0 ? (
            <p className="text-sm text-gray-400">Todavía no hay ventas registradas en este turno</p>
          ) : (
            <>
              <div className="space-y-1.5">
                {efectivo.map(([medioPago, monto]) => (
                  <div key={medioPago} className="flex justify-between items-center text-sm bg-green-50 border border-green-200 rounded-md px-2 py-1.5">
                    <span className="flex items-center gap-1.5 text-green-700 font-medium">
                      <MdPayments className="text-base" />
                      {formatMedioPago(medioPago)}
                    </span>
                    <span className="font-semibold text-green-700">{formatCurrency(monto)}</span>
                  </div>
                ))}
                {otros.map(([medioPago, monto]) => (
                  <div key={medioPago} className="flex justify-between text-sm px-2 py-0.5">
                    <span className="text-gray-500">{formatMedioPago(medioPago)}</span>
                    <span className="font-medium text-gray-600">{formatCurrency(monto)}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-2">
                Solo el efectivo suma al <span className="font-medium">efectivo esperado en caja</span>. Los demás medios no requieren arqueo.
              </p>
            </>
          )}
        </div>

        {/* Solo hay composición cuando viene el desglose de ingresos en efectivo (turnos abiertos y cierres con el
            cálculo nuevo). Un turno cerrado antes del cambio no lo trae y su composición no cuadraría con el esperado. */}
        {turno.totalIngresosEfectivo != null && (
        <div className="pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setMostrarComposicion((v) => !v)}
            className="w-full flex items-center justify-between text-left"
          >
            <p className="text-xs font-semibold text-gray-600">Cómo se compone el efectivo esperado</p>
            {mostrarComposicion ? <MdExpandLess className="text-lg text-gray-500" /> : <MdExpandMore className="text-lg text-gray-500" />}
          </button>
          {mostrarComposicion && (
            <>
              <div className="space-y-1 text-sm mt-2">
                <div className="flex justify-between text-gray-600">
                  <span>Monto inicial</span>
                  <span className="font-medium">{formatCurrency(montoInicial)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>+ Efectivo de ventas</span>
                  <span className="font-medium">{formatCurrency(efectivoVentas)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>+ Ingresos en efectivo <span className="text-gray-400">(ingresos extra, abonos de fiado)</span></span>
                  <span className="font-medium">{formatCurrency(ingresosEfectivo)}</span>
                </div>
                {ingresosAprobados > ingresosEfectivo && (
                  <div className="flex justify-between text-gray-400 text-xs pl-3">
                    <span>Ingresos con otros medios (no suman al efectivo)</span>
                    <span>{formatCurrency(ingresosAprobados - ingresosEfectivo)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>− Egresos aprobados <span className="text-gray-400">(retiros, gastos)</span></span>
                  <span className="font-medium">{formatCurrency(egresosAprobados)}</span>
                </div>
                <div className="flex justify-between pt-1.5 mt-1 border-t border-gray-200 font-semibold" style={{ color: '#11194F' }}>
                  <span>= Efectivo esperado</span>
                  <span style={{ color: '#22C55E' }}>{formatCurrency(efectivoEsperado)}</span>
                </div>
              </div>
              <p className="text-[11px] text-gray-400 mt-2">
                Solo se cuentan los movimientos ya <span className="font-medium">aprobados</span> — los pendientes de aprobación no afectan este cálculo todavía.
              </p>
            </>
          )}
        </div>
        )}
      </CardContent>
    </Card>
  );
};
